import React from 'react'

import { useQuery } from '@apollo/client'
import {
  Avatar,
  Box,
  CircularProgress,
  Grid,
  Skeleton,
  Typography,
} from '@mui/material'
import { useTheme } from '@mui/material/styles'

import styles from './EnterprisePlanReport.module.scss'
import ImpactSection from './ImpactSection'
import OverviewSection from './OverviewSection'
import SignupsSection from './SignupsSection'
import UsageSection from './UsageSection'
import { EnterprisePlanDocument } from '../../../__generated__/graphql'

interface EnterprisePlanReportProps {
  enterprisePlanId: number
}

// Helper component for section headers
function SectionHeader({ title, shaded }: { title: string; shaded?: boolean }) {
  return (
    <h2 className={`${styles.sectionHeader} ${shaded ? styles.shaded : ''}`}>
      {title}
    </h2>
  )
}

export default function EnterprisePlanReport({
  enterprisePlanId,
}: EnterprisePlanReportProps) {
  const theme = useTheme()

  const { data: currentPlan, loading: loadingCurrentPlan } = useQuery(
    EnterprisePlanDocument,
    {
      variables: {
        enterprisePlanInput: {
          id: enterprisePlanId,
        },
      },
    },
  )

  return (
    <Box className={styles.container}>
      {/* Header */}
      <Box className={styles.header}>
        <Grid alignItems="center" container gap={1.5}>
          <Grid>
            <Avatar
              className={styles.avatar}
              sx={{
                backgroundColor: theme.palette.primary.light,
              }}
            >
              {loadingCurrentPlan && <CircularProgress aria-hidden />}
              {currentPlan &&
                currentPlan.enterprisePlan.metadata.orgName.charAt(0)}
            </Avatar>
          </Grid>
          <Grid>
            {loadingCurrentPlan && <Skeleton sx={{ minWidth: 200 }} />}
            {currentPlan && (
              <Typography
                component="div"
                variant="h4"
                className={styles.orgName}
              >
                {currentPlan.enterprisePlan.metadata.orgName}
              </Typography>
            )}
          </Grid>
        </Grid>
      </Box>

      {/* Overview Section */}
      <SectionHeader title="Overview" shaded />
      <Box className={styles.section}>
        <OverviewSection
          loading={loadingCurrentPlan}
          orgData={currentPlan?.enterprisePlan.metadata}
          orgId={currentPlan?.enterprisePlan.id}
        />
      </Box>
      <Box className={styles.section}>
        <SectionHeader title="Adoption" />

        <SignupsSection
          loading={loadingCurrentPlan}
          totalSignups={currentPlan?.enterprisePlan.userSignups.total}
          userSignups={currentPlan?.enterprisePlan.userSignups.userSignups}
          itemsPerPage={10}
        />
      </Box>

      {/* Metrics Playground Section */}
      <SectionHeader title="Metrics Playground" shaded />

      <Box className={styles.section}>
        <SectionHeader title="Impact" />
        <ImpactSection
          loading={loadingCurrentPlan}
          numUsersPerBucket={
            currentPlan?.enterprisePlan.impact.protectionScore.numUsersPerBucket
          }
          numUsersPerBucketPerConcernArea={
            currentPlan?.enterprisePlan.impact.protectionScore
              .numUsersPerBucketPerConcernArea
          }
          timeSaved={currentPlan?.enterprisePlan.impact.timeSaved}
        />
      </Box>

      <Box className={styles.section}>
        <SectionHeader title="Usage" />
        <UsageSection
          loading={loadingCurrentPlan}
          checklistsUsed={currentPlan?.enterprisePlan.usage.checklistsUsed}
          recommendationsUpdated={
            currentPlan?.enterprisePlan.usage.recommendationsUpdated
          }
          contentDeleted={currentPlan?.enterprisePlan.usage.contentDeleted}
          connectionsRemoved={
            currentPlan?.enterprisePlan.usage.connectionsRemoved
          }
        />
      </Box>
    </Box>
  )
}
